import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, bandColor } from "@/lib/api";
import { Shell, CTGrid, StatCard } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Award, LayoutDashboard, CheckCircle2, XCircle, ChevronDown } from "lucide-react";

const LETTERS = ["A", "B", "C", "D", "E", "F"];

function QuestionRow({ row, index }) {
  const [open, setOpen] = useState(false);
  const correct = row.is_correct;
  const answered = row.selected_index !== null && row.selected_index !== undefined;
  const options = row.options || [];
  const preview = (row.question_text || "").length > 90
    ? `${row.question_text.slice(0, 90)}…`
    : (row.question_text || "Question");

  return (
    <div
      className={`rounded-2xl border transition-colors ${correct ? "border-emerald-200" : "border-blue-200"} bg-white overflow-hidden`}
      data-testid={`qrow-${index}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 text-left hover:bg-slate-50/70 transition-colors"
        data-testid={`qrow-toggle-${index}`}
      >
        <div className={`h-9 w-9 shrink-0 rounded-xl flex items-center justify-center text-sm font-semibold ${correct ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>
          {index + 1}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-slate-400 font-medium">Question {index + 1}</p>
          <p className="text-sm text-slate-700 truncate">{preview}</p>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1.5 text-xs sm:text-sm px-3 py-1.5 rounded-full font-medium ${correct ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>
          {correct
            ? <><CheckCircle2 className="h-4 w-4" /> Correct</>
            : <><XCircle className="h-4 w-4" /> Incorrect</>}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 sm:px-5 pb-5 pt-1 border-t border-slate-100" data-testid={`qrow-detail-${index}`}>
          <p className="text-[15px] font-medium text-slate-900 mt-4">{row.question_text}</p>

          <div className="mt-4 space-y-2.5">
            {options.map((opt, i) => {
              const isCorrect = i === row.correct_index;
              const isSelected = i === row.selected_index;
              let cls = "border-slate-200 bg-white";
              if (isCorrect) cls = "border-emerald-300 bg-emerald-50";
              else if (isSelected) cls = "border-blue-300 bg-blue-50";
              return (
                <div key={i} className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${cls}`}>
                  <span className={`h-6 w-6 shrink-0 rounded-md flex items-center justify-center text-xs font-semibold ${isCorrect ? "bg-emerald-100 text-emerald-700" : isSelected ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>
                    {LETTERS[i]}
                  </span>
                  <span className="text-sm text-slate-700 flex-1">{opt}</span>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {isCorrect && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Correct answer
                      </span>
                    )}
                    {isSelected && !isCorrect && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700">
                        <XCircle className="h-3.5 w-3.5" /> Your answer
                      </span>
                    )}
                    {isSelected && isCorrect && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">Your answer</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className={`rounded-xl px-4 py-3 ${correct ? "bg-emerald-50" : "bg-blue-50"}`}>
              <p className="text-xs text-slate-500">Your Answer</p>
              <p className={`text-sm font-medium ${correct ? "text-emerald-800" : "text-blue-800"}`}>
                {answered ? `${LETTERS[row.selected_index]}. ${options[row.selected_index]}` : "Not answered"}
              </p>
            </div>
            <div className="rounded-xl px-4 py-3 bg-emerald-50">
              <p className="text-xs text-slate-500">Correct Answer</p>
              <p className="text-sm font-medium text-emerald-800">
                {`${LETTERS[row.correct_index]}. ${options[row.correct_index]}`}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ResultPage() {
  const { attemptId } = useParams();
  const [a, setA] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get(`/attempts/${attemptId}`).then((r) => setA(r.data)); }, [attemptId]);

  if (!a) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading result…</div>;

  const answers = a.answers || [];

  return (
    <Shell title="Test Result" subtitle={`${a.test_title} · ${a.chapter_name}`} nav={
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="result-back-btn">
        <LayoutDashboard className="h-4 w-4 mr-1" />Back
      </Button>
    }>
      <div className="soft-card rounded-3xl p-8 flex flex-col sm:flex-row items-center gap-8" data-testid="overall-card">
        <div className="h-28 w-28 rounded-full bg-accent flex flex-col items-center justify-center shrink-0">
          <span className="text-3xl font-head font-bold text-primary" data-testid="overall-percentage">{a.percentage}%</span>
          <span className="text-xs text-slate-500">score</span>
        </div>
        <div className="text-center sm:text-left">
          <div className="flex items-center gap-2 justify-center sm:justify-start">
            <Award className="h-5 w-5 text-primary" />
            <span className={`text-sm px-3 py-1 rounded-full border ${bandColor(a.band)}`} data-testid="overall-band">{a.band}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-head font-bold mt-3">
            You scored {a.correct} out of {a.total_questions}
          </h2>
          <p className="text-slate-600 mt-2 text-sm">Here is how you did across the four thinking skills.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <StatCard label="Total Questions" value={a.total_questions} testid="res-total" />
        <StatCard label="Correct" value={a.correct} testid="res-correct" />
        <StatCard label="Incorrect" value={a.incorrect} testid="res-incorrect" />
        <StatCard label="Percentage" value={`${a.percentage}%`} testid="res-percentage" />
      </div>

      <h3 className="text-xl font-head font-semibold mt-10 mb-4">CT Competency Overview</h3>
      <CTGrid ct={a.ct_results} testid="result-ct-grid" />

      <div className="mt-10" data-testid="question-wise-section">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
          <h3 className="text-xl font-head font-semibold">Question-Wise Performance</h3>
          <p className="text-sm text-slate-500">Tap any question to review it</p>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          <span className="inline-flex items-center gap-1 text-emerald-700 mr-3"><CheckCircle2 className="h-4 w-4" /> Correct</span>
          <span className="inline-flex items-center gap-1 text-blue-700"><XCircle className="h-4 w-4" /> Incorrect</span>
        </p>
        <div className="space-y-3">
          {answers.map((row, i) => (
            <QuestionRow key={row.question_id || i} row={row} index={i} />
          ))}
        </div>
      </div>

      <div className="mt-10">
        <Button className="rounded-full" onClick={() => navigate("/student")} data-testid="result-dashboard-btn">Back to Dashboard</Button>
      </div>
    </Shell>
  );
}
